import { useUnitDistances, sortByDistance, DistanceBadge } from "@/components/gps/useUnitDistances";
import { useState, useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FONT = "'IBM Plex Sans', sans-serif";

/* ─── Shared submenu panel style ─── */
const panelStyle = {
  background: "linear-gradient(180deg, #F0F4F8 0%, #D8E0EC 100%)",
  border: "2px solid #6688BB",
  borderRadius: 4,
  boxShadow: "0 6px 24px rgba(0,0,0,0.5)",
  fontFamily: FONT,
  minWidth: 220,
};
const subItemStyle = {
  padding: "8px 16px", fontSize: 14, color: "#222", cursor: "pointer",
  borderBottom: "1px solid #C8D0DC",
};
const subHover = (e, on) => { e.currentTarget.style.background = on ? "#B8C8E0" : "transparent"; };

/* ─── Modal shell ─── */
function ModalPanel({ title, onClose, onOk, children, width }) {
  return (
    <div style={{ ...panelStyle, minWidth: width || 320, maxWidth: width || 400, position: "relative" }}>
      <div style={{ background: "linear-gradient(135deg, #4A5A8A, #3A4A7A)", padding: "6px 12px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid #5577AA" }}>
        <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 14, color: "#FFF" }}>{title}</span>
        <div data-testid="submenu-modal-close" onClick={onClose} style={{ width: 20, height: 20, background: "#CC3333", borderRadius: 3, color: "#FFF", fontWeight: 700, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>X</div>
      </div>
      <div style={{ padding: 14 }}>{children}</div>
      {onOk && (
        <div style={{ padding: "8px 14px", borderTop: "1px solid #AAB8CC", textAlign: "center" }}>
          <button data-testid="submenu-modal-ok" onClick={onOk} style={{ padding: "5px 30px", background: "linear-gradient(180deg, #D8E0F0, #B0C0D8)", border: "1px solid #8899BB", borderRadius: 3, fontFamily: FONT, fontSize: 13, fontWeight: 600, color: "#333", cursor: "pointer" }}>OK</button>
        </div>
      )}
    </div>
  );
}

const labelSt = { fontSize: 13, fontWeight: 600, color: "#333", minWidth: 110, textAlign: "right", paddingRight: 8 };
const inputSt = { flex: 1, padding: "4px 6px", border: "1px solid #99AABB", borderRadius: 2, fontSize: 13, background: "#FFF", outline: "none", fontFamily: FONT };
const selectSt = { ...inputSt, cursor: "pointer", appearance: "auto" };
const rowSt = { display: "flex", alignItems: "center", marginBottom: 8 };

/* ═══════════════════════════════════════
   ASSIGN UNIT SUBMENU
   ═══════════════════════════════════════ */
export function AssignUnitSubmenu({ units, gpsPositions, job, locationCoords, onSelect, onClose }) {
  const AVAILABLE_SIGNALS = [97, 98, 99, 89];
  const available = units.filter((u) => u.logged_in && AVAILABLE_SIGNALS.includes(u.signal || 97));

  const dist = useUnitDistances(job?.lat ?? locationCoords?.[0], job?.lng ?? locationCoords?.[1], gpsPositions);
  const sorted = sortByDistance(available, dist);

  return (
    <div style={{ ...panelStyle, minWidth: 280 }}>
      <div style={{ padding: "6px 12px", borderBottom: "1px solid #AAB8CC", fontWeight: 700, fontSize: 13, color: "#333" }}>Assign Unit</div>
      {sorted.length === 0 && <div style={{ padding: 12, color: "#888", fontSize: 13 }}>No available units</div>}
      {sorted.map((u) => {
        return (
          <div key={u.id} data-testid={`assign-${u.name}`} style={{ ...subItemStyle, display: "flex", justifyContent: "space-between", alignItems: "center" }}
            onMouseEnter={(e) => subHover(e, true)} onMouseLeave={(e) => subHover(e, false)}
            onClick={() => { onSelect(u.id); onClose(); }}>
            <span>{u.name}</span>
            <DistanceBadge d={dist[u.id]} testId={`assign-dist-${u.name}`} />
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════
   CHANGE STATUS SUBMENU
   ═══════════════════════════════════════ */
export function ChangeStatusSubmenu({ onSelect, onClose, job, fireMode = false }) {
  const [haHbOpen, setHaHbOpen] = useState(null); // "HA" or "HB"
  const [hospitals, setHospitals] = useState([]);
  const [category, setCategory] = useState("");
  const [hospital, setHospital] = useState("");
  const [override, setOverride] = useState("");
  // Track which hospital is the "closest" one for the selected category so
  // we can require an override reason only when the dispatcher picks a
  // different hospital. When the closest is kept, override defaults to
  // "CLOSEST" and the dispatcher doesn't have to pick anything.
  const [closestHospitalId, setClosestHospitalId] = useState("");
  // Active diversions — fetched live so the closest-hospital picker can
  // skip a hospital that's currently on diversion for the chosen category.
  const [activeDiversions, setActiveDiversions] = useState([]);

  // EMS status set (existing). Fire mode swaps in the operator-spec set:
  //   Responding (RESP, GREEN), 10-84 (RED), 10-08 (WHITE).
  // Each Fire entry sends a typed payload up to handleChangeStatus so the
  // backend can record both the displayed code and the underlying signal.
  const statuses = fireMode ? [
    { code: "RESP",  label: "RESPONDING", signal: 63, color: "#00CC00", clickable: true },
    { code: "10-84", label: "ON SCENE",   signal: 84, color: "#FF3030", clickable: true },
    { code: "10-08", label: "IN SERVICE", signal: 97, color: "#FFFFFF", clickable: true, bg: "#222" },
  ] : [
    { code: "10-63", signal: 63, clickable: true },
    { code: "10-84", signal: 84, clickable: true },
    { code: "HA", clickable: false, hasSubmenu: true },
    { code: "HB", clickable: false, hasSubmenu: true },
    { code: "10-81", signal: 81, clickable: true },
    { code: "10-98", signal: 98, clickable: true },
  ];

  const categories = [
    "A - Adult GED", "B - Critical Adult", "C - Critical Peds", "D - OBS",
    "E - STROKE", "F - BURN", "G - STEMI", "H - LVAD", "I - SAFE",
    "J - Peds EDP", "K - Adult EDP", "L - Peds TRAUMA", "M - Adult TRAUMA",
    "N - HYPERBARIC", "O - Peds GED", "P - VENOM", "Q - LVO", "T - TRANSPORT",
  ];

  const overrides = [
    "PATIENT CHOICE", "CLOSEST", "SUPV", "OLMC", "ALTERNATE DESTINATION",
  ];

  useEffect(() => {
    if (haHbOpen) {
      axios.get(`${API}/hospitals`).then((r) => setHospitals(r.data)).catch(() => {});
      axios.get(`${API}/diversions`, { params: { active_only: true } })
        .then((r) => setActiveDiversions(r.data || []))
        .catch(() => setActiveDiversions([]));
    }
  }, [haHbOpen]);

  // Haversine distance (miles) — matches backend MCI helper.
  const getDistanceMiles = (lat1, lng1, lat2, lng2) => {
    if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
    const R = 3958.8;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  };

  // Normalize a string for loose capability matching — mirrors the backend
  // MCI `_normalize` helper so closest-hospital selection stays consistent
  // with the MCI auto-router.
  const normalize = (s) => (s || "").toUpperCase().replace(/[^A-Z0-9]+/g, " ").replace("CRTITICAL", "CRITICAL").trim();

  // Does a hospital accept the given category string? Matches by letter
  // prefix ("A") or by normalized name substring ("Adult GED").
  const hospitalAcceptsCategory = (h, catStr) => {
    if (!catStr) return false;
    const letter = (catStr.split("-")[0] || "").trim().toUpperCase();
    const catName = normalize(catStr.includes("-") ? catStr.split("-").slice(1).join("-") : catStr);
    const caps = (h.capabilities || []).map(normalize);
    return caps.some((c) => {
      if (!c) return false;
      if (letter && c.startsWith(letter + " ")) return true;
      if (letter && c === letter) return true;
      if (catName && (c === catName || c.includes(catName) || catName.includes(c))) return true;
      return false;
    });
  };

  // Is a (hospital, category) pair currently on diversion? Strict match —
  // normalized category must equal the diversion's normalized category
  // EXACTLY (no substring matching, since "Adult ED" vs "Adult EDP" are
  // different categories). The picker letter prefix ("K - Adult EDP") is
  // stripped before matching.
  const isOnDiversion = (hospitalId, catStr) => {
    if (!hospitalId || !catStr || !activeDiversions.length) return false;
    const catN = normalize(catStr.includes("-") ? catStr.split("-").slice(1).join("-") : catStr);
    if (!catN) return false;
    return activeDiversions.some((d) => {
      if (d.hospital_id !== hospitalId) return false;
      return normalize(d.category || "") === catN;
    });
  };

  // When the category OR the hospital list changes, auto-pick the closest
  // matching hospital (if the job has coords) and default the override to
  // "CLOSEST". Hospitals on active diversion for that category are skipped
  // — the unit is automatically routed to the next-closest accepting one.
  useEffect(() => {
    if (!category || hospitals.length === 0) {
      setClosestHospitalId("");
      return;
    }
    const refLat = job?.lat;
    const refLng = job?.lng;
    if (refLat == null || refLng == null) {
      setClosestHospitalId("");
      return;
    }
    let best = null;
    let bestD = Infinity;
    for (const h of hospitals) {
      if (!hospitalAcceptsCategory(h, category)) continue;
      if (h.lat == null || h.lng == null) continue;
      // Skip diverted hospitals — auto-reroute to the next closest.
      if (isOnDiversion(h.id, category)) continue;
      const d = getDistanceMiles(refLat, refLng, h.lat, h.lng);
      if (d != null && d < bestD) { bestD = d; best = h; }
    }
    if (best) {
      setClosestHospitalId(best.id);
      // Auto-select the closest hospital + default override to "CLOSEST".
      setHospital(best.id);
      setOverride("CLOSEST");
    } else {
      setClosestHospitalId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, hospitals, job?.lat, job?.lng, activeDiversions]);

  // When the dispatcher changes the hospital manually, clear the override
  // if they moved AWAY from the closest one so they are forced to pick a
  // reason. If they switch back to the closest, default to "CLOSEST" again.
  // If they pick a hospital that's currently on diversion for the chosen
  // category, auto-reroute to the next-closest accepting one and surface a
  // warning so they know what happened.
  const handleHospitalChange = (e) => {
    const val = e.target.value;
    if (val && category && isOnDiversion(val, category)) {
      const div = activeDiversions.find((d) => d.hospital_id === val);
      const until = div ? new Date(div.until) : null;
      const untilStr = until ? `${String(until.getHours()).padStart(2, "0")}${String(until.getMinutes()).padStart(2, "0")}` : "";
      const replacedName = (hospitals.find((h) => h.id === closestHospitalId) || {}).name || "the next-closest hospital";
      // eslint-disable-next-line no-alert
      alert(`That hospital is on diversion for ${category.split("-").slice(1).join("-").trim() || category} until ${untilStr}.\nRerouting to ${replacedName}.`);
      setHospital(closestHospitalId);
      setOverride("CLOSEST");
      return;
    }
    setHospital(val);
    if (closestHospitalId && val === closestHospitalId) {
      setOverride("CLOSEST");
    } else {
      setOverride("");
    }
  };

  const handleHaHbOk = () => {
    if (!category || !hospital || !override) return;
    const hosp = hospitals.find((h) => h.id === hospital);
    onSelect({
      type: "hospital_transport",
      haHb: haHbOpen,
      category,
      hospital_id: hospital,
      hospital_code: hosp ? (hosp.hospital_code || "") : "",
      hospital_name: hosp ? hosp.name : "",
      override,
    });
    onClose();
  };

  return (
    <div style={{ position: "relative" }}>
      <div style={{ ...panelStyle, minWidth: 180 }}>
        <div style={{ padding: "6px 12px", borderBottom: "1px solid #AAB8CC", fontWeight: 700, fontSize: 13, color: "#333" }}>
          {fireMode ? "Change Status (Fire)" : "Change Status"}
        </div>
        {statuses.map((s) => (
          <div key={s.code} data-testid={`status-${s.code}`}
            style={{
              ...subItemStyle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              ...(fireMode && s.color ? {
                color: s.color === "#FFFFFF" ? "#FFF" : s.color,
                background: s.bg || (s.color === "#FFFFFF" ? "#222" : undefined),
                fontWeight: 700,
                letterSpacing: 0.5,
              } : {}),
            }}
            onMouseEnter={(e) => { subHover(e, true); if (s.hasSubmenu) setHaHbOpen(s.code); }}
            onMouseLeave={(e) => subHover(e, false)}
            onClick={() => {
              if (s.clickable) {
                if (fireMode) {
                  // Send a typed payload so DispatchBoard knows the unit
                  // is a Fire apparatus and the operator-spec colored code
                  // (RESP / 10-84 / 10-08) should land in unit_history.
                  onSelect({ type: "fire_status", code: s.code, label: s.label, signal: s.signal });
                } else {
                  onSelect(s.signal || s.code);
                }
                onClose();
              }
              if (s.hasSubmenu) setHaHbOpen(s.code);
            }}>
            <span>{s.code}{fireMode && s.label ? <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.85 }}>({s.label})</span> : null}</span>
            {s.hasSubmenu && <span style={{ color: "#666", fontSize: 12 }}>&#9654;</span>}
          </div>
        ))}
      </div>

      {/* HA/HB Modal with 3 dropdowns */}
      {haHbOpen && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 2999 }} onClick={(e) => { e.stopPropagation(); setHaHbOpen(null); }} />
          <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 3000 }}
            onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
            <ModalPanel title={`${haHbOpen} - HOSPITAL TRANSPORT`} onClose={() => setHaHbOpen(null)} onOk={handleHaHbOk} width={500}>
            <div style={rowSt}>
              <span style={labelSt}>Category:</span>
              <select data-testid="hahb-category" value={category} onChange={(e) => setCategory(e.target.value)} style={{ ...selectSt, color: "#000" }}>
                <option value="">Select...</option>
                {categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={rowSt}>
              <span style={labelSt}>Hospital:</span>
              <select data-testid="hahb-hospital" value={hospital} onChange={handleHospitalChange} style={{ ...selectSt, color: "#000" }}>
                <option value="">Select...</option>
                {hospitals
                  // Hide hospitals on active diversion for the chosen
                  // category — only that category is suppressed for them.
                  // The hospital reappears under any other category they
                  // accept, and reappears for this one once the diversion
                  // expires (the active_only fetch auto-prunes).
                  .filter((h) => !category || !isOnDiversion(h.id, category))
                  .map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.hospital_code || ""} - {h.name}
                      {closestHospitalId === h.id ? "  ★ CLOSEST" : ""}
                    </option>
                  ))}
              </select>
            </div>
            <div style={rowSt}>
              <span style={labelSt}>Override:</span>
              <select data-testid="hahb-override" value={override} onChange={(e) => setOverride(e.target.value)} style={{ ...selectSt, color: "#000" }}>
                <option value="">Select...</option>
                {overrides.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            {(!category || !hospital || !override) && (
              <div style={{ fontSize: 11, color: "#CC0000", textAlign: "center", marginTop: 4 }}>All three fields required</div>
            )}
          </ModalPanel>
          </div>
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════
   UPDATE STATUS SUBMENU
   ═══════════════════════════════════════ */
export function UpdateStatusSubmenu({ onSelect, onClose, fireMode = false }) {
  // Fire mode swaps the EMS extension list for the operator-spec Fire
  // pair (Extended Operations / Assisting EMS) AND inlines a Comment Box
  // that submits alongside the chosen status.
  const items = fireMode
    ? ["EXTENDED OPERATIONS", "ASSISTING EMS"]
    : [
        "EXTENDED TRAFFIC/WEATHER",
        "EXTENDED PATIENT CARE",
        "EXTENDED TRIAGE",
        "EXTENDED PAPERWORK",
        "EXTENDED STRETCHER",
      ];
  const [selected, setSelected] = useState("");
  const [comment, setComment] = useState("");

  if (!fireMode) {
    return (
      <div style={{ ...panelStyle, minWidth: 260 }}>
        <div style={{ padding: "6px 12px", borderBottom: "1px solid #AAB8CC", fontWeight: 700, fontSize: 13, color: "#333" }}>Update Status</div>
        {items.map((item) => (
          <div key={item} data-testid={`update-${item.split(" ")[1]}`} style={subItemStyle}
            onMouseEnter={(e) => subHover(e, true)} onMouseLeave={(e) => subHover(e, false)}
            onClick={() => { onSelect(item); onClose(); }}>
            {item}
          </div>
        ))}
      </div>
    );
  }

  // Fire mode — radio-select item + comment textarea + OK button.
  return (
    <div style={{ ...panelStyle, minWidth: 320, padding: 8 }} onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
      <div style={{ padding: "4px 8px", borderBottom: "1px solid #AAB8CC", fontWeight: 700, fontSize: 13, color: "#7A3A00", marginBottom: 6 }}>
        Update Status (Fire)
      </div>
      {items.map((item) => (
        <label key={item} style={{ display: "flex", alignItems: "center", padding: "6px 8px", cursor: "pointer", fontSize: 13, gap: 8, borderRadius: 2 }}>
          <input
            type="radio"
            name="fire-update-status"
            data-testid={`fire-update-${item.replace(/\s+/g, "_").toLowerCase()}`}
            checked={selected === item}
            onChange={() => setSelected(item)}
          />
          <span>{item}</span>
        </label>
      ))}
      <div style={{ marginTop: 6, padding: "4px 8px" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#666", marginBottom: 3 }}>Comment</div>
        <textarea
          data-testid="fire-update-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          rows={3}
          placeholder="Optional details (visible in unit history)..."
          style={{ width: "100%", boxSizing: "border-box", fontSize: 12, padding: 6, border: "1px solid #AAB8CC", borderRadius: 2 }}
        />
      </div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, padding: "0 8px 6px 8px" }}>
        <button
          data-testid="fire-update-cancel"
          onClick={() => onClose()}
          style={{ padding: "4px 12px", fontSize: 12, background: "#888", color: "#FFF", border: "none", borderRadius: 2, cursor: "pointer" }}
        >Cancel</button>
        <button
          data-testid="fire-update-ok"
          disabled={!selected}
          onClick={() => {
            onSelect({ type: "fire_update_status", status: selected, comment });
            onClose();
          }}
          style={{ padding: "4px 14px", fontSize: 12, fontWeight: 700, background: selected ? "#1E5F9C" : "#AAA", color: "#FFF", border: "none", borderRadius: 2, cursor: selected ? "pointer" : "not-allowed" }}
        >OK</button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   ADD UNIT TO CALL SUBMENU (Assist)
   Shows only signal-98 available units sorted by Haversine distance from the
   call's geocoded lat/lng. Excludes units already attached to the call.
   ═══════════════════════════════════════ */
export function AddUnitToCallSubmenu({ units, gpsPositions, job, locationCoords, onSelect, onClose }) {
  // Resolve the call's reference coords: prefer job.lat/lng, fall back to locationCoords
  // (dispatcher's currently-typed CAD location) if the job hasn't been geocoded yet.
  const refLat = job?.lat ?? (locationCoords ? locationCoords[0] : null);
  const refLng = job?.lng ?? (locationCoords ? locationCoords[1] : null);

  // FIRE_UNIT_TYPES mirror — keep this in sync with backend FIRE_UNIT_TYPES.
  const FIRE_TYPES = new Set(["ENGINE", "BRUSH FIRE UNIT", "BATTALION CHIEF"]);
  const isFireUnit = (u) => FIRE_TYPES.has(((u && u.unit_type) || "").toUpperCase());

  // When the call is FIRE, render two sections so the dispatcher can pull
  // EMS in alongside the Fire units (operator spec). Otherwise behave as
  // the legacy single-list submenu.
  const isFireCall = ((job?.service_type) || "EMS").toUpperCase() === "FIRE";

  // Only AVAILABLE units (97/98/99/89)
  const AVAILABLE_SIGNALS_ASSIST = [97, 98, 99, 89];
  const attachedIds = new Set([
    job?.assigned_unit_id,
    ...((job?.assist_units || []).map((a) => (a?.unit_id ? a.unit_id : a))),
  ].filter(Boolean));
  const available = units.filter((u) => u.logged_in && AVAILABLE_SIGNALS_ASSIST.includes(u.signal || 97) && !attachedIds.has(u.id));

  const dist = useUnitDistances(refLat, refLng, gpsPositions);
  const sortByDist = (a, b) => {
    const da = dist[a.id]?.miles, db = dist[b.id]?.miles;
    if (da == null && db == null) return a.name.localeCompare(b.name);
    if (da == null) return 1;
    if (db == null) return -1;
    return da - db;
  };

  const renderRow = (u) => {
    return (
      <div key={u.id} data-testid={`addunit-${u.name}`}
        style={{ ...subItemStyle, display: "flex", justifyContent: "space-between", alignItems: "center" }}
        onMouseEnter={(e) => subHover(e, true)} onMouseLeave={(e) => subHover(e, false)}
        onClick={() => { onSelect(u.id); onClose(); }}>
        <span>{u.name}<span style={{ marginLeft: 6, fontSize: 10, color: "#888" }}>{u.unit_type || ""}</span></span>
        <DistanceBadge d={dist[u.id]} testId={`addunit-dist-${u.name}`} />
      </div>
    );
  };

  if (!isFireCall) {
    const sorted = [...available].sort(sortByDist);
    return (
      <div style={{ ...panelStyle, minWidth: 280 }}>
        <div style={{ padding: "6px 12px", borderBottom: "1px solid #AAB8CC", fontWeight: 700, fontSize: 13, color: "#333" }}>Add Unit to Call</div>
        {sorted.length === 0 && <div style={{ padding: 12, color: "#888", fontSize: 13 }}>No available units</div>}
        {sorted.map(renderRow)}
      </div>
    );
  }

  // FIRE call → split into FIRE + EMS sections.
  const fireList = available.filter(isFireUnit).sort(sortByDist);
  const emsList = available.filter((u) => !isFireUnit(u)).sort(sortByDist);

  return (
    <div style={{ ...panelStyle, minWidth: 320, maxHeight: "85vh", overflow: "auto" }}>
      <div style={{ padding: "6px 12px", borderBottom: "1px solid #AAB8CC", fontWeight: 700, fontSize: 13, color: "#7A3A00", background: "#FFE0CC" }}>
        Add Unit to Call (Fire)
      </div>
      <div data-testid="addunit-fire-section">
        <div style={{ padding: "4px 12px", background: "#FFD0B0", fontSize: 11, fontWeight: 700, color: "#7A3A00", letterSpacing: 1 }}>FIRE UNITS</div>
        {fireList.length === 0 && <div style={{ padding: 8, color: "#888", fontSize: 12 }}>No available Fire units</div>}
        {fireList.map(renderRow)}
      </div>
      <div data-testid="addunit-ems-section" style={{ borderTop: "1px solid #AAB8CC" }}>
        <div style={{ padding: "4px 12px", background: "#D0E0F0", fontSize: 11, fontWeight: 700, color: "#003B66", letterSpacing: 1 }}>EMS UNITS</div>
        {emsList.length === 0 && <div style={{ padding: 8, color: "#888", fontSize: 12 }}>No available EMS units</div>}
        {emsList.map(renderRow)}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   MCI MODAL
   ═══════════════════════════════════════ */
export function MCIModal({ job, onClose, onSubmit }) {
  const [stagingLocation, setStagingLocation] = useState("");
  const [comments, setComments] = useState("");

  // Force black text color on every input inside this modal (per user spec).
  const blackInputSt = { ...inputSt, color: "#000" };

  const categories = [
    "A - Adult GED", "B - Critical Adult", "C - Critical Ped",
    "F - Burn", "L - Peds Trauma", "M - Adult Trauma",
    "N - Hyperbaric", "O - Peds GED",
  ];

  return (
    <ModalPanel title="MCI INFORMATION" onClose={onClose} onOk={() => { onSubmit({ address: job?.location || "", stagingLocation, comments, categories }); onClose(); }}>
      <div style={rowSt}>
        <span style={labelSt}>Address:</span>
        <input data-testid="mci-address" style={blackInputSt} value={job?.location || ""} readOnly />
      </div>
      <div style={rowSt}>
        <span style={labelSt}>Staging Location:</span>
        <input data-testid="mci-staging" style={blackInputSt} value={stagingLocation} onChange={(e) => setStagingLocation(e.target.value)} placeholder="Enter staging location..." />
      </div>
      <div style={{ border: "1px solid #99AABB", borderRadius: 2, marginBottom: 8, maxHeight: 180, overflow: "auto" }}>
        <div style={{ padding: "4px 8px", fontWeight: 700, fontSize: 12, color: "#333", borderBottom: "1px solid #AAB8CC", background: "#D8E0EC" }}>Categories</div>
        {categories.map((c) => (
          <div key={c} style={{ padding: "4px 10px", fontSize: 13, color: "#333", borderBottom: "1px solid #E0E4EC" }}>{c}</div>
        ))}
      </div>
      <div style={rowSt}>
        <span style={labelSt}>Comments:</span>
        <textarea data-testid="mci-comments" style={{ ...blackInputSt, minHeight: 50, resize: "vertical" }} value={comments} onChange={(e) => setComments(e.target.value)} />
      </div>
    </ModalPanel>
  );
}

// ─── Fire-side dispositions (operator spec) ───
// Each entry: dispositionLabel → list of code options. NO CODE entries
// are represented as { code: "", label: "..." } so the dropdown still
// has a row for them. An empty `codes` list means the disposition has
// no code variants and the dispatcher just picks the disposition.
const FIRE_DISPOSITIONS = [
  {
    label: "10-25 Manhole or transformer vault fire",
    codes: [
      { code: "", label: "NO CODE — Situation other than 1, 2, 3, or 4" },
      { code: "CODE 1", label: "Fire extended into building" },
      { code: "CODE 2", label: "One or more covers blown, under pressure" },
      { code: "CODE 3", label: "Smoke seeping from manhole" },
      { code: "CODE 4", label: "Transformer fire (manhole or pole-mounted)" },
    ],
  },
  { label: "10-26 Food on stove", codes: [] },
  { label: "10-27 Compactor or incinerator fire", codes: [] },
  {
    label: "10-28 Subway/railroad fire, emergency or smoke",
    codes: [
      { code: "CODE 1", label: "Structural fire" },
      { code: "CODE 2", label: "Non-structural fire (train, rubbish, etc.)" },
      { code: "CODE 3", label: "Non-medical emergency" },
    ],
  },
  {
    label: "10-29 Elevator emergency",
    codes: [
      { code: "CODE 1", label: "Occupied" },
      { code: "CODE 2", label: "Unoccupied" },
    ],
  },
  { label: "10-31 Assist civilian — non-medical", codes: [] },
  { label: "10-32 Defective oil burner", codes: [] },
  {
    label: "10-33 Odor of smoke",
    codes: [
      { code: "CODE 1", label: "Caused by nearby working fire, BBQs, salamanders, etc." },
      { code: "CODE 2", label: "Any other type odor" },
    ],
  },
  {
    label: "10-34 Sprinkler system emergency",
    codes: [
      { code: "CODE 1", label: "Defective equipment" },
      { code: "CODE 2", label: "Unwarranted alarm" },
      { code: "CODE 3", label: "Heat activated" },
    ],
  },
  {
    label: "10-35 Unwarranted/unnecessary alarm activation",
    codes: [
      { code: "", label: "NO CODE — Unwarranted alarm" },
      { code: "CODE 1", label: "Caused by alarm system testing or servicing" },
      { code: "CODE 2", label: "Caused by construction activities" },
      { code: "CODE 3", label: "Caused by ordinary household activities" },
      { code: "CODE 4", label: "Other known causes (e.g. smoking in unauthorized areas)" },
    ],
  },
  {
    label: "10-36 Automobile accident or emergency",
    codes: [
      { code: "", label: "NO CODE — Vehicle emergency other than below" },
      { code: "CODE 1", label: "Washdown" },
      { code: "CODE 2", label: "Accident — no injuries or washdown" },
      { code: "CODE 3", label: "Accident with injuries" },
      { code: "CODE 4", label: "Accident requiring extrication" },
    ],
  },
  {
    label: "10-37 Medical assignment (non-fire ops)",
    codes: [
      { code: "CODE 1", label: "Victim deceased (non-fire related)" },
      { code: "CODE 2", label: "Victim not breathing — requires resuscitation" },
      { code: "CODE 3", label: "Victim breathing with illness or injury" },
      { code: "CODE 4", label: "Unit 10-84, no patient contact, EMS on scene" },
    ],
  },
  {
    label: "10-38 Carbon monoxide response",
    codes: [
      { code: "CODE 1", label: "Detector activation (defective, low-battery, unwarranted)" },
      { code: "CODE 2", label: "CO incident — readings 1-9 PPM" },
      { code: "CODE 3", label: "CO emergency — readings > 9 PPM" },
      { code: "CODE 4", label: "No detector activation during incident" },
    ],
  },
  { label: "10-39 FD standby at interagency incident", codes: [] },
  {
    label: "10-40 Utility emergency",
    codes: [
      { code: "CODE 1", label: "Gas" },
      { code: "CODE 2", label: "Electric" },
      { code: "CODE 3", label: "Water condition" },
      { code: "CODE 4", label: "Steam leak" },
    ],
  },
  {
    label: "10-41 Suspicious fire",
    codes: [
      { code: "CODE 1", label: "Occupied building or vehicle" },
      { code: "CODE 2", label: "Unoccupied building" },
      { code: "CODE 3", label: "Unoccupied vehicle" },
      { code: "CODE 4", label: "Vacant structure or non-dwelling structures" },
    ],
  },
  { label: "10-42 Downed tree incident or emergency", codes: [] },
  { label: "10-43 Non-fire-related rescue", codes: [] },
  { label: "10-91 Medical Emergency EMS — Fire Unit Not Required", codes: [] },
  { label: "10-92 Malicious false alarm", codes: [] },
];

/* ═══════════════════════════════════════
   DISPOSITIONS SUBMENU
   ═══════════════════════════════════════ */
export function DispositionsSubmenu({ onSelect, onClose, fireMode = false }) {
  const [comment, setComment] = useState("");
  const [selected, setSelected] = useState(null);
  // Fire-mode adds a code-dropdown for dispositions that have variants.
  const [selectedCode, setSelectedCode] = useState("");

  // EMS dispositions (existing behavior).
  const emsDispositions = [
    "10-83D Dead On Arrival",
    "10-83R Dead After Resuscitation Efforts",
    "10-87 Call Cancelled",
    "10-90 Patient Unfounded",
    "10-91 FD/PD Matter",
    "10-93 Refuse Medical Attention",
    "10-94 One Unit Treated Another Transported",
    "10-95 Triaged Out By Telemetry",
    "10-96 Gone Prior To EMS Arrival",
  ];

  const fireSelectedEntry = fireMode
    ? FIRE_DISPOSITIONS.find((d) => d.label === selected)
    : null;

  const handleOk = () => {
    if (!selected) return;
    if (fireMode) {
      // Code is required when the disposition has code variants. NO CODE
      // is encoded as code === "" with a label entry in the dropdown.
      const codes = fireSelectedEntry?.codes || [];
      if (codes.length > 0 && selectedCode === null) return;
      onSelect({
        disposition: selected,
        code: selectedCode || "",
        comment,
        fire: true,
      });
    } else {
      onSelect({ disposition: selected, comment });
    }
    onClose();
  };

  const list = fireMode ? FIRE_DISPOSITIONS.map((d) => d.label) : emsDispositions;

  return (
    <div style={{ ...panelStyle, minWidth: fireMode ? 480 : 360, maxHeight: "85vh", overflow: "auto" }}>
      <div style={{ padding: "6px 12px", borderBottom: "1px solid #AAB8CC", fontWeight: 700, fontSize: 13, color: fireMode ? "#7A3A00" : "#333" }}>
        {fireMode ? "Dispositions (Fire)" : "Dispositions"}
      </div>
      {list.map((d) => (
        <div key={d} data-testid={`disp-${d.split(" ")[0]}`}
          style={{ ...subItemStyle, background: selected === d ? (fireMode ? "#FFE0CC" : "#B8C8E0") : "transparent", borderLeft: selected === d ? `3px solid ${fireMode ? "#C06030" : "#3366AA"}` : "3px solid transparent", fontWeight: selected === d ? 700 : 400 }}
          onMouseEnter={(e) => subHover(e, true)} onMouseLeave={(e) => { if (selected !== d) subHover(e, false); }}
          onClick={() => { setSelected(d); setSelectedCode(""); }}>
          {d}
        </div>
      ))}
      {/* Fire-mode CODE dropdown — appears once a disposition with codes is picked. */}
      {fireMode && fireSelectedEntry && fireSelectedEntry.codes.length > 0 && (
        <div style={{ padding: "8px 12px", borderTop: "1px solid #AAB8CC", background: "#FFF8F0" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#7A3A00", marginBottom: 4 }}>Code:</div>
          <select
            data-testid="fire-disp-code"
            value={selectedCode}
            onChange={(e) => setSelectedCode(e.target.value)}
            style={{ width: "100%", padding: "4px 6px", border: "1px solid #99AABB", borderRadius: 2, fontSize: 13, color: "#000", background: "#FFF" }}
          >
            {fireSelectedEntry.codes.map((c, i) => (
              <option key={i} value={c.code}>
                {c.code ? `${c.code} — ${c.label}` : c.label}
              </option>
            ))}
          </select>
        </div>
      )}
      <div style={{ padding: "8px 12px", borderTop: "1px solid #AAB8CC" }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4 }}>Dispatcher Comments (optional):</div>
        <textarea
          data-testid="dispo-comment"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Enter disposition comments..."
          style={{ width: "100%", minHeight: 50, padding: "6px 8px", border: "1px solid #99AABB", borderRadius: 2, fontSize: 13, fontFamily: FONT, resize: "vertical", outline: "none", boxSizing: "border-box", color: "#000", background: "#FFF" }}
        />
      </div>
      <div style={{ padding: "8px 12px", borderTop: "1px solid #AAB8CC", display: "flex", justifyContent: "center", gap: 12 }}>
        <button data-testid="dispo-ok" onClick={handleOk} disabled={!selected}
          style={{ padding: "5px 30px", background: selected ? "linear-gradient(180deg, #D8E0F0, #B0C0D8)" : "#CCC", border: "1px solid #8899BB", borderRadius: 3, fontFamily: FONT, fontSize: 13, fontWeight: 600, color: selected ? "#333" : "#999", cursor: selected ? "pointer" : "not-allowed" }}>
          OK
        </button>
        <button data-testid="dispo-cancel" onClick={onClose}
          style={{ padding: "5px 30px", background: "linear-gradient(180deg, #F0F0F0, #D8D8D8)", border: "1px solid #999", borderRadius: 3, fontFamily: FONT, fontSize: 13, fontWeight: 600, color: "#333", cursor: "pointer" }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   CSL ASSIGN SUBMENU (Available Units right-click)
   ═══════════════════════════════════════ */
export function CSLAssignSubmenu({ onSelect, onClose }) {
  const locations = [
    "BROADWAY JUNCTION",
    "BUFFALO AND EASTERN PARKWAY",
    "ROCKAWAY AVE AND LINDEN BLVD",
    "BEDFORD AVE AND ATLANTIC AVENUE",
    "LAFAYETTE AVENUE AND MALCOLM X BLVD",
    "TROY AVENUE AND ATLANTIC AVENUE",
  ];

  return (
    <div style={panelStyle}>
      <div style={{ padding: "6px 12px", borderBottom: "1px solid #AAB8CC", fontWeight: 700, fontSize: 13, color: "#333" }}>
        Assign Cross Street Location
      </div>
      {locations.map((loc) => (
        <div key={loc} data-testid={`csl-${loc.replace(/\s+/g, "-").toLowerCase()}`} style={subItemStyle}
          onMouseEnter={(e) => subHover(e, true)} onMouseLeave={(e) => subHover(e, false)}
          onClick={() => { onSelect(loc); onClose(); }}>
          {loc}
        </div>
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════
   NOTIFICATION MODAL
   ═══════════════════════════════════════ */
export function NotificationModal({ onClose, onSubmit }) {
  const [hospitals, setHospitals] = useState([]);
  const [notifType, setNotifType] = useState("");
  const [hospital, setHospital] = useState("");
  const [hospitalQuery, setHospitalQuery] = useState("");
  const [showHospDropdown, setShowHospDropdown] = useState(false);
  const [number, setNumber] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("");
  const [bp, setBp] = useState("");
  const [pulse, setPulse] = useState("");
  const [resp, setResp] = useState("");
  const [ocs, setOcs] = useState("");
  const [mentals, setMentals] = useState("");
  const [bgl, setBgl] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    axios.get(`${API}/hospitals`).then((r) => setHospitals(r.data)).catch(() => {});
  }, []);

  // Force black text color on every input inside this modal (per user spec).
  const blackInputSt = { ...inputSt, color: "#000" };
  const blackSelectSt = { ...selectSt, color: "#000" };

  const pickHospital = (h) => {
    setHospital(h.name);
    setHospitalQuery(h.name);
    setShowHospDropdown(false);
    // Auto-fill the notification number from the hospital record (uploaded Excel).
    setNumber(h.notification_number || "");
  };

  const filteredHospitals = hospitalQuery
    ? hospitals.filter((h) => {
        const q = hospitalQuery.toLowerCase();
        return (
          (h.name || "").toLowerCase().includes(q) ||
          (h.abbreviation || "").toLowerCase().includes(q) ||
          (h.hospital_code || "").toLowerCase().includes(q)
        );
      }).slice(0, 20)
    : hospitals.slice(0, 20);

  return (
    <ModalPanel
      title="NOTIFICATION INFORMATION"
      onClose={onClose}
      onOk={() => {
        onSubmit({
          notification_type: notifType,
          hospital,
          number,
          gender,
          age,
          blood_pressure: bp,
          pulse,
          respirations: resp,
          ocs,
          mentals,
          bgl,
          notes,
        });
        onClose();
      }}
      width={400}
    >
      <div style={rowSt}><span style={labelSt}>Reason:</span>
        <select data-testid="notif-type" style={blackSelectSt} value={notifType} onChange={(e) => setNotifType(e.target.value)}>
          <option value="">Select...</option>
          <option value="MEDICAL">MEDICAL</option>
          <option value="TRAUMA">TRAUMA</option>
          <option value="SPECIAL ACCOMMODATION">SPECIAL ACCOMMODATION</option>
        </select>
      </div>

      {/* Hospital: searchable combobox. Type to filter; click a row to select. */}
      <div style={{ ...rowSt, position: "relative", alignItems: "flex-start" }}>
        <span style={{ ...labelSt, paddingTop: 5 }}>Hospital:</span>
        <div style={{ flex: 1, position: "relative" }}>
          <input
            data-testid="notif-hospital-search"
            style={blackInputSt}
            value={hospitalQuery}
            placeholder="Type hospital name / code / abbreviation..."
            onFocus={() => setShowHospDropdown(true)}
            onChange={(e) => { setHospitalQuery(e.target.value); setShowHospDropdown(true); setHospital(""); setNumber(""); }}
          />
          {showHospDropdown && filteredHospitals.length > 0 && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#FFF", border: "1px solid #99AABB", borderRadius: 2, maxHeight: 180, overflowY: "auto", zIndex: 20 }}>
              {filteredHospitals.map((h) => (
                <div
                  key={h.id}
                  data-testid={`notif-hosp-opt-${h.hospital_code || h.name}`}
                  style={{ padding: "4px 8px", cursor: "pointer", color: "#000", borderBottom: "1px solid #EEE", fontSize: 12 }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#EAF2FF")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#FFF")}
                  onMouseDown={(e) => { e.preventDefault(); pickHospital(h); }}
                >
                  <span style={{ fontWeight: 600 }}>{h.hospital_code || h.abbreviation || ""}</span>
                  <span style={{ marginLeft: 6, color: "#333" }}>{h.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={rowSt}><span style={labelSt}>Number:</span><input data-testid="notif-number" style={blackInputSt} value={number} readOnly /></div>
      <div style={rowSt}><span style={labelSt}>Gender:</span>
        <select data-testid="notif-gender" style={blackSelectSt} value={gender} onChange={(e) => setGender(e.target.value)}>
          <option value="">Select...</option>
          <option value="MALE">MALE</option>
          <option value="FEMALE">FEMALE</option>
          <option value="X">X</option>
        </select>
      </div>
      <div style={rowSt}><span style={labelSt}>Age:</span><input data-testid="notif-age" style={blackInputSt} value={age} onChange={(e) => setAge(e.target.value)} /></div>
      <div style={rowSt}><span style={labelSt}>Blood Pressure:</span><input data-testid="notif-bp" style={blackInputSt} value={bp} onChange={(e) => setBp(e.target.value)} /></div>
      <div style={rowSt}><span style={labelSt}>Pulse:</span><input data-testid="notif-pulse" style={blackInputSt} value={pulse} onChange={(e) => setPulse(e.target.value)} /></div>
      <div style={rowSt}><span style={labelSt}>Respirations:</span><input data-testid="notif-resp" style={blackInputSt} value={resp} onChange={(e) => setResp(e.target.value)} /></div>
      <div style={rowSt}><span style={labelSt}>OCS:</span><input data-testid="notif-ocs" style={blackInputSt} value={ocs} onChange={(e) => setOcs(e.target.value)} /></div>
      <div style={rowSt}><span style={labelSt}>Mentals:</span>
        <select data-testid="notif-mentals" style={blackSelectSt} value={mentals} onChange={(e) => setMentals(e.target.value)}>
          <option value="">Select...</option>
          <option value="AxOx1">AxOx1</option>
          <option value="AxOx2">AxOx2</option>
          <option value="AxOx3">AxOx3</option>
          <option value="AxOx4">AxOx4</option>
        </select>
      </div>
      <div style={rowSt}><span style={labelSt}>BGL:</span><input data-testid="notif-bgl" style={blackInputSt} value={bgl} onChange={(e) => setBgl(e.target.value)} /></div>
      <div style={rowSt}><span style={labelSt}>Notes:</span><textarea data-testid="notif-notes" style={{ ...blackInputSt, minHeight: 50, resize: "vertical" }} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
    </ModalPanel>
  );
}
